import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { ROLE_LABEL, WEEKDAYS } from "@/utils/status";

export default function SettingsPage() {
  const { user, refreshMe } = useAuth();
  const [firstName, setFirstName] = useState(user?.first_name || "");
  const [lastName, setLastName] = useState(user?.last_name || "");
  const [password, setPassword] = useState("");
  const [wh, setWh] = useState(user?.working_hours || {});
  const isAdmin = user?.role === "admin";
  const canEditHours = user?.role === "manager";

  useEffect(() => {
    setFirstName(user?.first_name || "");
    setLastName(user?.last_name || "");
    setWh(user?.working_hours || {});
  }, [user]);

  const save = async () => {
    try {
      const payload = { first_name: firstName, last_name: lastName };
      if (canEditHours) payload.working_hours = wh;
      if (password) payload.password = password;
      await api.patch(`/users/${user.id}`, payload);
      toast.success("Profilo aggiornato");
      setPassword("");
      refreshMe();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setSlot = (day, idx, field, val) => {
    const next = { ...wh, [day]: [...(wh[day] || [])] };
    next[day][idx] = { ...next[day][idx], [field]: val };
    setWh(next);
  };
  const addSlot = (day) => setWh({ ...wh, [day]: [...(wh[day] || []), { start: "09:00", end: "13:00" }] });
  const removeSlot = (day, idx) => setWh({ ...wh, [day]: (wh[day] || []).filter((_, i) => i !== idx) });

  return (
    <div className="p-8 h-full overflow-y-auto max-w-3xl">
      <h1 className="font-display text-3xl font-bold tracking-tight">Impostazioni</h1>
      <p className="text-muted-foreground text-sm mt-1 mb-6">Personalizza il tuo profilo e le fasce orarie.</p>

      <Card className="mb-4">
        <CardHeader><CardTitle className="font-display text-lg">Profilo</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="h-14 w-14 rounded-full flex items-center justify-center font-bold text-lg text-white" style={{ backgroundColor: user?.color }}>
              {(user?.name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase()}
            </div>
            <div>
              <div className="font-semibold">{user?.email}</div>
              <Badge variant="secondary" className="mt-1">{ROLE_LABEL[user?.role]}</Badge>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Cognome</Label>
              <Input value={lastName} onChange={(e) => setLastName(e.target.value)} data-testid="profile-last-name-input" />
            </div>
            <div>
              <Label>Nome</Label>
              <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} data-testid="profile-name-input" />
            </div>
          </div>
          <div>
            <Label>Nuova password (opzionale)</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="profile-password-input" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="font-display text-lg">Fasce orarie personali</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {isAdmin ? (
            <div className="text-sm text-muted-foreground italic py-2">
              Gli account Admin non hanno fasce orarie: la disponibilità è gestita solo per gli account che compaiono nei calendari (Manager, Consulente, Trainer).
            </div>
          ) : !canEditHours ? (
            <div className="space-y-1" data-testid="working-hours-readonly">
              {WEEKDAYS.map(({ key, label }) => (
                <div key={key} className="flex items-center gap-3 text-sm border rounded-md px-2 py-1.5">
                  <div className="w-12 font-semibold">{label}</div>
                  <div className="text-muted-foreground">{(wh[key] || []).map((s) => `${s.start}–${s.end}`).join(", ") || "Non disponibile"}</div>
                </div>
              ))}
              <div className="text-xs text-muted-foreground italic pt-1">Le fasce orarie possono essere modificate solo da Admin e Manager.</div>
            </div>
          ) : WEEKDAYS.map(({ key, label }) => (
            <div key={key} className="flex items-start gap-3 border rounded-md p-2">
              <div className="w-12 font-semibold text-sm pt-1.5">{label}</div>
              <div className="flex-1 space-y-1">
                {(wh[key] || []).map((slot, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input type="time" value={slot.start} className="w-28" onChange={(e) => setSlot(key, idx, "start", e.target.value)} />
                    <span className="text-muted-foreground">–</span>
                    <Input type="time" value={slot.end} className="w-28" onChange={(e) => setSlot(key, idx, "end", e.target.value)} />
                    <Button variant="ghost" size="sm" onClick={() => removeSlot(key, idx)}>rimuovi</Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => addSlot(key)}>+ fascia</Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="mt-4">
        <Button onClick={save} data-testid="save-profile-button">Salva modifiche</Button>
      </div>
    </div>
  );
}
